


import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';

const OTPVerificationModal = ({
  isOpen,
  onClose,
  onSuccess,
  userEmail = 'john***@mail.com',
}) => {
  const [step, setStep] = useState('choose');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);

  // Countdown timer
  useEffect(() => {
    if (timer > 0) {
      const t = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [timer]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep('choose');
      setOtp(['', '', '', '', '', '']);
      setError('');
      setTimer(0);
      setSuccess(false);
    }
  }, [isOpen]);

  // Send OTP to email
  const sendOTP = async () => {
    if (!userEmail) {
      setError('Email not provided');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('https://api.nfcdesignstudio.com/API/v1/sendotpp', { email: userEmail });
      setStep('input');
      setTimer(300); // 5 minutes
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) {
      setError('Enter complete OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('https://api.nfcdesignstudio.com/API/v1/otpverification', {
        email: userEmail,
        otp: code,
      });
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1200);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const copy = [...otp];
    copy[i] = v.slice(-1);
    setOtp(copy);
    setError('');
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={styles.backdrop}
        onClick={onClose}
      />
      <div style={styles.wrapper}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          style={styles.modal}
        >
          {/* Header */}
          <div style={styles.header}>
            <div>
              <h3 style={styles.title}>🔐 Verify to Edit</h3>
              <p style={styles.subtitle}>Confirm your identity</p>
            </div>
            <button onClick={onClose} style={styles.iconBtn}>
              <X />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: 24 }}>
            {step === 'choose' && (
              <>
                <p style={styles.label}>Verify via Email</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={sendOTP}
                  disabled={loading}
                  style={styles.primaryBtn}
                >
                  {loading ? <Loader2 className="spin" /> : `Send OTP to ${userEmail}`}
                </motion.button>
              </>
            )}

            {step === 'input' && (
              <>
                <p style={{ textAlign: 'center' }}>
                  OTP sent to <b>{userEmail}</b>
                </p>

                <div style={styles.otpRow}>
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      value={d}
                      maxLength={1}
                      onChange={(e) => handleChange(i, e.target.value)}
                      style={styles.otpInput}
                    />
                  ))}
                </div>

                {error && (
                  <div style={styles.error}>
                    <AlertCircle size={18} /> {error}
                  </div>
                )}

                <motion.button
                  onClick={handleVerify}
                  disabled={loading}
                  whileHover={{ scale: 1.05 }}
                  style={styles.primaryBtn}
                >
                  {loading ? <Loader2 className="spin" /> : 'Verify'}
                </motion.button>

                {success && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    style={styles.success}
                  >
                    <Check size={32} />
                    <p>Verified</p>
                  </motion.div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OTPVerificationModal;


/* styles */
const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    zIndex: 50,
  },
  wrapper: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 51,
  },
  modal: {
    background: '#fff',
    borderRadius: 20,
    width: '100%',
    maxWidth: 420,
    overflow: 'hidden',
  },
  header: {
    padding: 20,
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e5e7eb',
  },
  title: { margin: 0 },
  subtitle: { fontSize: 13, color: '#6b7280' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer' },
  label: { fontWeight: 600, marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  methodBtn: {
    padding: 16,
    borderRadius: 16,
    border: '2px solid',
    cursor: 'pointer',
    background: '#f9fafb',
  },
  primaryBtn: {
    width: '100%',
    marginTop: 20,
    padding: 14,
    borderRadius: 16,
    border: 'none',
    background: '#6366f1',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
  otpRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    margin: '16px 0',
  },
  otpInput: {
    width: 44,
    height: 52,
    fontSize: 22,
    textAlign: 'center',
    borderRadius: 12,
    border: '2px solid #e5e7eb',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#dc2626',
    marginTop: 10,
    fontSize: 14,
  },
  success: {
    marginTop: 16,
    textAlign: 'center',
    color: '#16a34a',
  },
};

